import { DocumentStatus } from '@synergy/models';

import { Invoice } from '../models/invoice';
import { SkippedInvoiceReason } from '../invoice-api';

/** The one state an invoice may be completed from. */
export const COMPLETABLE_STATUSES = [DocumentStatus.Created];

/**
 * Why an invoice in the selection will not be completed.
 *
 * The document itself refuses completion on four counts (see
 * Invoice::completionBlocker): the state it is in, a credit that is not
 * negative, an item without a tax rate, and a missing order number where the
 * customer demands one. Two of them can be answered here, and two cannot —
 * the list carries neither the items' tax rates nor the customer's flag. Those
 * two the server reports back as skipped after the fact.
 *
 * So this is what makes the dialog honest about what it can see, not what
 * makes the batch correct. The server remains the authority.
 */
export const localBlocker = (invoice: Invoice): SkippedInvoiceReason | undefined => {
    if (!COMPLETABLE_STATUSES.includes(invoice.status)) {
        return 'status';
    }

    if (invoice.isCredit && invoice.totalNetPrice > 0) {
        return 'creditAmount';
    }

    return undefined;
};

/**
 * Receipts the batch would complete over nothing.
 *
 * A document over 0,00 € takes a number out of the range, goes out by mail and
 * lands in the books saying nothing. It is not wrong enough to refuse — a
 * cancelled order can legitimately settle at zero — but it is wrong often
 * enough that nobody should pass it without looking (#2210, R-107).
 */
export const zeroValued = (invoices: Invoice[]): Invoice[] =>
    invoices.filter(invoice => 0 === Math.round(invoice.totalPrice * 100));

/**
 * What the batch adds up to, per currency.
 *
 * Summing across currencies would produce a number that means nothing, so each
 * currency keeps its own line.
 *
 * Gross, like the column the same receipts are read from a moment earlier: a
 * dialog that answers the list with a different number invites the reader to
 * work out which of the two is the real one (#2210, R-108).
 */
export const totalsByCurrency = (invoices: Invoice[]): Record<string, number> =>
    invoices.reduce<Record<string, number>>((all, invoice) => {
        all[invoice.currency] = (all[invoice.currency] ?? 0) + invoice.totalPrice;

        return all;
    }, {});
